import { effect, Injectable } from '@angular/core';
import { AppAngularBaseClass } from "@app/core/services/app-angular-base";

// Define interfaces for log payloads
interface ErrorLogPayload {
  message?: string;
  user: string;
  url: string;
  timestamp: string;
  errorCode?: string;
  stackTrace?: string;
  context?: any;
  severity?: string;
  partId: number | null;
  data?: any;
}

interface InfoLogPayload {
  user: string;
  url: string;
  timestamp: string;
  message?: string;
  context?: any;
  partId: number | null;
  data?: any;
}

interface WarningLogPayload {
  user: string;
  url: string;
  timestamp: string;
  message?: string;
  context?: any;
  severity?: string;
  partId: number | null;
  data?: any;
}

interface DebugLogPayload {
  user: string;
  url: string;
  timestamp: string;
  message?: string;
  context?: any;
  partId: number | null;
  data?: any;
}

type LogLevel = 'error' | 'warning' | 'info' | 'debug';

@Injectable({ providedIn: 'root' })
export class LoggingService extends AppAngularBaseClass {
  partId: number | null = null;
  userName: string = '';
  logLevel: LogLevel = 'info'; // Default log level

  private logLevelOrder: Record<LogLevel, number> = {
    error: 0,
    warning: 1,
    info: 2,
    debug: 3,
  };

  constructor() {
    super();
    effect(() => {
      this.partId = this.user.partId();
    });

    effect(() => {
      this.userName = this.user.userFriendlyName();
    });
  }

  private buildContext(scope: string): any {
    const stack = new Error().stack?.split('\n');
    // Try to extract caller info from stack trace
    const caller = stack && stack.length > 3 ? stack[3].trim() : '';
    return {
      scope,
      component: this.getComponentName(),
      method: this.getCallerMethod(caller),
      browser: navigator.userAgent,
      appVersion: (window as any).appVersion || '',
      environment: (window as any).environment || '',
      correlationId: this.getCorrelationId(),
      url: window.location.href,
    };
  }

  private getComponentName(): string {
    // Optionally, use Angular's injector or naming conventions
    // For now, fallback to scope
    return '';
  }

  private getCallerMethod(caller: string): string {
    // Extract method name from stack trace line
    const match = caller.match(/at (\w+)/);
    return match ? match[1] : '';
  }

  private getCorrelationId(): string {
    // Generate or retrieve a correlation ID
    return (window as any).correlationId || '';
  }

  private shouldLog(level: LogLevel): boolean {
    return this.logLevelOrder[level] <= this.logLevelOrder[this.logLevel];
  }

  private logToConsole(level: 'error' | 'info' | 'warning' | 'debug', payload: any): void {
     if (!this.shouldLog(level)) return;

    const styles: Record<string, string> = {
      error: 'color: white; background: #d32f2f; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      warning: 'color: black; background: #ffeb3b; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      info: 'color: white; background: #1976d2; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      debug: 'color: white; background: #388e3c; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
      log: 'color: black; background: #e0e0e0; font-weight: bold; padding: 2px 6px; border-radius: 3px;',
    };

    const label = `%c[${level.toUpperCase()}]`;
    const style = styles[level] || styles['log'];

    // Print main message with styling, then print the full payload object for inspection
    /* eslint-disable no-restricted-syntax */
    if (payload.message) {
      console.log(label, style, payload.message);
      console.dir(payload);
    } else {
      console.log(label, style, payload);
    }
    /* eslint-disable no-restricted-syntax */
  }

  error(error: any, scope: string = 'bits3', data?: any): void {
    const errorData: ErrorLogPayload = {
      message: typeof error === 'string' ? error : error.message,
      user: this.userName,
      partId: this.partId,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      errorCode: error.code,
      stackTrace: error.stack,
      context: this.buildContext(scope),
      severity: 'high',
      data: data,
    };
    this.logToConsole('error', errorData);
  }

  info(info: any, scope: string = 'bits3', data?: any): void {
    const infoData: InfoLogPayload = {
      message: typeof info === 'string' ? info : info.message,
      user: this.userName,
      partId: this.partId,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      context: this.buildContext(scope),
      data: data,
    };
    this.logToConsole('info', infoData);
  }

  warning(warning: any, scope: string = 'bits3', data?: any): void {
    const warningData: WarningLogPayload = {
      message: typeof warning === 'string' ? warning : warning.message,
      user: this.userName,
      partId: this.partId,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      context: this.buildContext(scope),
      severity: 'medium',
      data: data,
    };
    this.logToConsole('warning', warningData);
  }

  debug(debug: any, scope: string = 'bits3', data?: any): void {
    const debugData: DebugLogPayload = {
      message: typeof debug === 'string' ? debug : debug.message,
      user: this.userName,
      partId: this.partId,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      context: this.buildContext(scope),
      data: data,
    };
    this.logToConsole('debug', debugData);
  }
}
